import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { compose } from 'recompose'
import _ from 'lodash'

import Card from './Card'
import {
  withEditHandlers
} from 'components/custom/EditIconButton/index'
import { withCollapse } from 'components/fulcrum/collapse/Collapse'
import {
  HORIZONTAL_HEADERS,
  VERTICAL_HEADERS,
  DEFAULT_WIDTH_SECTIONS,
  DEFAULT_WIDTH,
  SECTION_DETAILS,
} from './constants'

class CardWrapper extends Component {
  generateRange = (rowStart, rowEnd, colStart, colEnd) => {
    return {
      'rowStart': rowStart,
      'rowEnd': rowEnd,
      'colStart': colStart,
      'colEnd': colEnd
    }
  }

  getTitleRanges = (sectionStart, sectionEnd, sectionKey) => {
    let titleRange = {}
    const { productSpecs } = this.props
    titleRange['rowStart'] = sectionStart - 1
    titleRange['rowEnd'] = sectionStart - 1

    let maxSectionWidth = 0
    while(sectionStart < sectionEnd) {
      let row = productSpecs[sectionStart] 
      const rowWidth = row.filter(cell => cell).length - 1
      maxSectionWidth = Math.max(rowWidth, maxSectionWidth)
      sectionStart += 1
    }

    titleRange['colStart'] = 0
    titleRange['colEnd'] = maxSectionWidth
    if (DEFAULT_WIDTH_SECTIONS.includes(sectionKey)) titleRange['colEnd'] = DEFAULT_WIDTH
    return [titleRange]
  }

  getHorizontalSubtitleRanges = (titleRange, sectionStart, sectionEnd, sectionKey) => {
    let subtitleRanges = []
    let subtitleRange = {}
    const { productSpecs } = this.props

    if (HORIZONTAL_HEADERS.includes(sectionKey)) {
      switch (sectionKey) {
        // overclock section has 2 adjacent rows of subtitles
        case SECTION_DETAILS.OVERCLOCK.key:
          subtitleRange = this.generateRange(titleRange['rowStart'] + 1, titleRange['rowEnd'] + 2, titleRange['colStart'], titleRange['colEnd'])
          subtitleRanges.push(Object.assign({}, subtitleRange))
          break
        // memory section has 2 non-adjacent rows of subtitles
        case SECTION_DETAILS.NVLINK.key:
        case SECTION_DETAILS.MEMORY.key:
          for (let i = sectionStart; i < sectionEnd; ++i) {
            const currKey = productSpecs[i].join('').replace(/\s/g, "")
            if (["rvl", "clock", "nvlinktopology", "nvlinkspeeds"].includes(currKey.toLowerCase())) {
              subtitleRange = this.generateRange(i, i, titleRange['colStart'], titleRange['colEnd'])
              subtitleRanges.push(Object.assign({}, subtitleRange))
            }
          }
          break
        // pcb section subtitle is two rows after the title  
        case SECTION_DETAILS.PCB.key:
          subtitleRange = this.generateRange(titleRange['rowStart'] + 2, titleRange['rowEnd'] + 2, titleRange['colStart'], titleRange['colEnd'])
          subtitleRanges.push(Object.assign({}, subtitleRange))
          break
        // for all other sections, subtitle is one row after the title
        default:
          subtitleRange = this.generateRange(titleRange['rowStart'] + 1, titleRange['rowEnd'] + 1, titleRange['colStart'], titleRange['colEnd'])
          subtitleRanges.push(Object.assign({}, subtitleRange))
          break
      }
    }
    return subtitleRanges
  }

  getVerticalSubtitleRanges = (sectionStart, sectionEnd, sectionKey) => {
    let subtitleRanges = []
    let subtitleRange = {}
    if (VERTICAL_HEADERS.includes(sectionKey)) { 
      subtitleRange = this.generateRange(sectionStart, sectionEnd, 0, 0)
      subtitleRanges.push(Object.assign({}, subtitleRange))
    }
    return subtitleRanges
  }

  getCheckboxRanges = (sectionStart, sectionEnd, sectionKey) => {
    let checkboxRanges = []
    const { productSpecs } = this.props
    // Enabled column of Supported Features section must be checkbox type
    if (sectionKey == SECTION_DETAILS.SUPPORTED_FEATURES.key) {
      checkboxRanges.push(this.generateRange(sectionStart + 1, sectionEnd - 1, 1, 1))
    }
    // All selectable colulmns in Form Elements section must be checkbox type
    else if(sectionKey == SECTION_DETAILS.FORM_ELEMENTS.key) {
      const headers = productSpecs[sectionStart].filter(spec => spec)
      const colStart = 2   // skip category and element columns
      checkboxRanges.push(this.generateRange(sectionStart + 1, sectionEnd, colStart, headers.length - 1))
    }
    // Default rows of vertical header sections must be checkboxes
    else if (VERTICAL_HEADERS.includes(sectionKey)) {
      for (let i = sectionStart; i < sectionEnd; ++i) {
        const header = productSpecs[i][0] && productSpecs[i][0].toLowerCase()
        // all defaults are checkboxes- except clock defaults which are numerical
        if (header && header.includes('default') && !header.includes('clock')) {  
          checkboxRanges.push(this.generateRange(i, i, 1, productSpecs[i].filter(spec => spec).length - 1))
        } 
      }
    }

    return checkboxRanges
  }

  getMergeRanges = (titleRange, sectionStart, sectionEnd, sectionKey) => {
    const { productSpecs } = this.props
    let mergeRanges = []
    let mergeRange = {}

    const genMergeRange = (row, col, rowspan, colspan) => {
      return {
        'row': row,
        'col': col,
        'rowspan': rowspan,
        'colspan': colspan
      }
    }
    
    // merge section title range
    mergeRange = genMergeRange(titleRange['rowStart'], titleRange['colStart'], 1, titleRange['colEnd'] - titleRange['colStart'] + 1)
    mergeRanges.push(Object.assign({}, mergeRange))

    switch (sectionKey) {
      case SECTION_DETAILS.OVERCLOCK.key:
        const clockFields = SECTION_DETAILS.OVERCLOCK.fields
        productSpecs[sectionStart].forEach((cell, index) => {
          if (cell && (cell.includes(clockFields.BASE_CLOCK) || cell.includes('Boost'))) {
            mergeRange = genMergeRange(sectionStart, index, 2, 1)
            mergeRanges.push(Object.assign({}, mergeRange))
          } else if (cell && cell.includes(clockFields.OC_BIN)) {
            mergeRange = genMergeRange(sectionStart, index, 1, 2)
            mergeRanges.push(Object.assign({}, mergeRange))
          }
        })
        break
      case SECTION_DETAILS.NVQUAL.key:
      case SECTION_DETAILS.NVLINK.key:
      case SECTION_DETAILS.MEMORY.key:
        const memoryFields = SECTION_DETAILS.MEMORY.fields
        const nvqualFields = SECTION_DETAILS.NVQUAL.fields
        const nvlinkFields = SECTION_DETAILS.NVLINK.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          if ([memoryFields.RVL, memoryFields.CLOCK, nvlinkFields.TOPOLOGY_TITLE, nvlinkFields.SPEED_TITLE, nvqualFields.THERMAL_TITLE].includes(productSpecs[i][0])) {
            mergeRange = genMergeRange(i, 0, 1, titleRange['colEnd'] - titleRange['colStart'] + 1)
            mergeRanges.push(Object.assign({}, mergeRange))
          } 
        }
        break
      case SECTION_DETAILS.FORM_ELEMENTS.key:
        let rowStart = sectionStart + 1
        for (let i = rowStart + 1; i <= sectionEnd; ++i) {
          if (productSpecs[i][0] || i == sectionEnd) {
            mergeRange = genMergeRange(rowStart, 0, i - rowStart, 1)
            if (mergeRange.rowspan > 1 || mergeRange.colspan > 1)
              mergeRanges.push(Object.assign({}, mergeRange))
            rowStart = i
          }
        }
        break
    }

    return mergeRanges
  }

  getDropdownCells = (titleRange, sectionStart, sectionEnd, sectionKey) => {
    const { productSpecs, specSources } = this.props
    const dropdownCells = []
    const sources = {}

    const genDropdownCell = (row, col, source) => {
      return {
        'row': row,
        'col': col,
        'source': source,
      }
    }

    switch (sectionKey) {
      case SECTION_DETAILS.VOLTAGE_REGULATOR.key:
        const vrFields = SECTION_DETAILS.VOLTAGE_REGULATOR.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case vrFields.RAIL_TYPE:
              sources[i] = specSources.voltageRegulator.railTypes
              break
            case vrFields.PART_TYPE: 
              sources[i] = specSources.voltageRegulator.regulatorParts.map(part => part.partType)
              break
            case vrFields.PART_NUMBER:
              sources[i] = specSources.voltageRegulator.regulatorParts.map(part => part.partNumber)
              break
            case vrFields.PHASE_COUNT:
              sources[i] = specSources.voltageRegulator.regulatorParts.map(part => part.phaseCount)
              break
            case vrFields.VENDOR_NAME:
              sources[i] = specSources.voltageRegulator.regulatorParts.map(part => part.vendorName)
              break
          }
        }
        break
      case SECTION_DETAILS.MEMORY.key:
        const memoryFields = SECTION_DETAILS.MEMORY.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case memoryFields.PART_NUMBER:
              sources[i] = specSources.memory.memoryParts.map(part => part.partNumber)
              break
            case memoryFields.ORGANIZATION_WIDTH:
              sources[i] = specSources.memory.memoryParts.map(part => part.organizationWidth)
              break
            case memoryFields.ORGANIZATION_SIZE:
              sources[i] = specSources.memory.memoryParts.map(part => part.organizationSize)
              break
            case memoryFields.VENDOR_NAME:
              sources[i] = specSources.memory.memoryParts.map(part => part.vendorName)
              break
            case memoryFields.PART_TYPE:
              sources[i] = specSources.memory.memoryPartTypes
              break
          }
        }
        break
      case SECTION_DETAILS.DISPLAY_CONNECTOR.key:
        const dcFields = SECTION_DETAILS.DISPLAY_CONNECTOR.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case dcFields.LINK_TYPE:
              sources[i] = specSources.displayConnector.linkTypes
              break
            case dcFields.CONNECTOR_TYPE: 
              sources[i] = specSources.displayConnector.connectorTypes
              break
            case dcFields.CONNECTOR_BRACKET:
              sources[i] = specSources.displayConnector.connectorBrackets
              break
            case dcFields.CONNECTOR_LOCATION:
              sources[i] = specSources.displayConnector.connectorLocations
              break
          }
        }
        break
      case SECTION_DETAILS.INPUT_CONNECTOR.key:
        const icFields = SECTION_DETAILS.INPUT_CONNECTOR.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case icFields.PIN_COUNT:
              sources[i] = specSources.inputConnector.inputConnectors
              break
          }
        }
        break
      case SECTION_DETAILS.PCB.key:
        const pcbFields = SECTION_DETAILS.PCB.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case pcbFields.TECHNOLOGY:
              dropdownCells.push(genDropdownCell(i, 1, specSources.pcb.pcbTechnologies))
              break
          }
        }
        break
      case SECTION_DETAILS.PCIE.key:
        const pcieFields = SECTION_DETAILS.PCIE.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case pcieFields.SPEED:
              sources[i] = specSources.pcIe.speeds
              break
            case pcieFields.WIDTH:
              sources[i] = specSources.pcIe.widths
              break
          }
        }
        break
      case SECTION_DETAILS.NVLINK.key:
        const nvlinkFields = SECTION_DETAILS.NVLINK.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case nvlinkFields.TOPOLOGIES:
              sources[i] = specSources.nvLink.topologies
              break
            case nvlinkFields.SPEEDS:
              sources[i] = specSources.nvLink.speeds
              break
          }
        }
        break
      case SECTION_DETAILS.NVQUAL.key:
        const nvqualFields = SECTION_DETAILS.NVQUAL.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case nvqualFields.TEST_TYPE:
              sources[i] = specSources.nvQual.testTypes
              break
          }
        }
        break
      case SECTION_DETAILS.TARGET_APPLICATION.key:
        const applicationFields = SECTION_DETAILS.TARGET_APPLICATION.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch (field) {
            case applicationFields.APPLICATION:
              sources[i] = specSources.targetApplication.application
              break
          }
        }
        break
      case SECTION_DETAILS.DEFAULT_CPU.key:
        const defaultCpuFields = SECTION_DETAILS.DEFAULT_CPU.fields
        for (let i = sectionStart; i < sectionEnd; ++i) {
          const field = productSpecs[i][0]
          switch  (field) {
            case defaultCpuFields.ARCHITECTURE:
              sources[i] = specSources.defaultCpu.architecture
              break
            case defaultCpuFields.CLASSIFICATION:
              sources[i] = specSources.defaultCpu.classification
              break
            case defaultCpuFields.BRAND:
              sources[i] = specSources.defaultCpu.brand
              break
          }
        }
    }

    for (let i = sectionStart; i < sectionEnd; ++i) {
      for (let j = 1; j <= titleRange.colEnd; ++j) {
        if (!_.isEmpty(sources[i])) { 
          dropdownCells.push(genDropdownCell(i, j, _.uniq(sources[i]).sort())) 
        }
      }
    }
      
    return dropdownCells
  }

  // returns an object containing ranges to be formatted appropriately by handsontable
  getFormattingRanges = () => {
    const {
      productSpecs,
      sectionHeaders
    } = this.props

    let formattingRanges = {
      titleRanges: [],
      horizontalSubtitleRanges: [],
      verticalSubtitleRanges: [],
      checkboxRanges: [],
      mergeRanges: [],
      dropdownCells: []
    }

    // section key is a camel-cased string with whitespace removed
    // this is required to help distinguish between sections on the form
    const getSectionKey = (index) => {
      const row = productSpecs.length && productSpecs[index]
      return row && _.camelCase(row.join('').replace(/\s/g, "")) 
    }

    let sectionKey = getSectionKey(0)
    let sectionStart = 1
    let sectionEnd = 0

    // console.log(productSpecs)
    for (let index = 1; index < productSpecs.length; ++index) {
      const currKey = getSectionKey(index)
      if (sectionHeaders.includes(currKey) || index == productSpecs.length - 1) {
        sectionEnd = (index == productSpecs.length - 1 ) ? index : index - 1
        const titleRanges = this.getTitleRanges(sectionStart, sectionEnd, sectionKey)
        const horizontalSubtitleRanges = this.getHorizontalSubtitleRanges(titleRanges[0], sectionStart, sectionEnd, sectionKey)
        const verticalSubtitleRanges = this.getVerticalSubtitleRanges(sectionStart, sectionEnd, sectionKey)
        const checkboxRanges = this.getCheckboxRanges(sectionStart, sectionEnd, sectionKey)
        const mergeRanges = this.getMergeRanges(titleRanges[0], sectionStart, sectionEnd, sectionKey)
        const dropdownCells = this.getDropdownCells(titleRanges[0], sectionStart, sectionEnd, sectionKey)

        if (titleRanges.length) 
          formattingRanges.titleRanges = titleRanges.concat(formattingRanges.titleRanges)
        if (horizontalSubtitleRanges.length) 
          formattingRanges.horizontalSubtitleRanges = horizontalSubtitleRanges.concat(formattingRanges.horizontalSubtitleRanges)
        if (verticalSubtitleRanges.length) 
          formattingRanges.verticalSubtitleRanges = verticalSubtitleRanges.concat(formattingRanges.verticalSubtitleRanges)
        if (checkboxRanges.length) 
          formattingRanges.checkboxRanges = checkboxRanges.concat(formattingRanges.checkboxRanges)
        if (mergeRanges.length) 
          formattingRanges.mergeRanges = mergeRanges.concat(formattingRanges.mergeRanges)
        if (dropdownCells.length) 
          formattingRanges.dropdownCells = dropdownCells.concat(formattingRanges.dropdownCells)

        sectionKey = currKey
        sectionStart = index + 1
      }
    }
    return formattingRanges
  }

  render() {
    const { hideSpecificationsTable } = this.props 
    return (
      !hideSpecificationsTable && 
      <Card 
        {...this.props} 
        formattingRanges={this.getFormattingRanges()}
      />
    )
  }
}

const enhance = compose(
  withRouter,
  withCollapse,
  withEditHandlers,
)
const CardWrapperSection = enhance(CardWrapper)

export default CardWrapperSection
